import { isNumber } from 'lodash';
import { type PerspectiveGroupingRule } from '@ardoq/perspectives';
import { GroupType } from '@ardoq/api-types';
import { isPerspectiveGroupingRuleWithWorkspace } from '@ardoq/perspectives-sidebar';
import { dateRangeOperations } from '@ardoq/date-range';
import { modelOperations } from '@ardoq/core';
import { type GroupingRuleLabelData } from './groupingRuleLabelData$';

export type GroupingRuleLabels = {
  workspaceName: string | null;
  targetLabel: string | null;
};

export type GroupingRuleLabelsByRuleId = Record<string, GroupingRuleLabels>;

const resolveTargetLabel = (
  rule: PerspectiveGroupingRule,
  labelData: GroupingRuleLabelData
): string | null => {
  if (!isPerspectiveGroupingRuleWithWorkspace(rule) || !rule.targetId) {
    return null;
  }

  switch (rule.type) {
    case GroupType.REFERENCE: {
      const referenceType = Number(rule.targetId);
      if (!isNumber(referenceType)) return null;
      const referenceTypes =
        labelData.referenceTypesByWorkspaceId[rule.workspaceId] ?? [];
      return modelOperations.getRefTypeName([...referenceTypes], referenceType);
    }
    case GroupType.PARENT: {
      const componentType = (
        labelData.componentTypesByWorkspaceId[rule.workspaceId] ?? []
      ).find(({ id }) => id === rule.targetId);
      return componentType?.name ?? null;
    }
    case GroupType.TAG: {
      const tag = labelData.tagsById[rule.targetId];
      return tag?.name ?? null;
    }
    case GroupType.FIELD: {
      const field = labelData.fieldsById[rule.targetId];
      if (!field) return null;
      return dateRangeOperations.dashFormatDateRangeFieldLabel(field.label);
    }
    default:
      return null;
  }
};

export const getGroupingRuleLabelsByRuleId = (
  groupingRules: PerspectiveGroupingRule[],
  labelData: GroupingRuleLabelData
): GroupingRuleLabelsByRuleId => {
  const result: GroupingRuleLabelsByRuleId = {};
  for (const rule of groupingRules) {
    if (!isPerspectiveGroupingRuleWithWorkspace(rule)) continue;
    result[rule.id] = {
      workspaceName: labelData.workspacesById[rule.workspaceId]?.name ?? null,
      targetLabel: resolveTargetLabel(rule, labelData),
    };
  }
  return result;
};
